/**
 * Flattens a sync result into one list of changes.
 *
 * The payload arrives grouped by team, with company-role changes and issues alongside. Rendering it
 * in that shape meant one collapsible section per team per change kind, each independently
 * paginated, which is unreviewable once a tenant has more than a handful of teams. One list with
 * filters answers the same questions and can be paged once.
 */

export type SyncResultTeamChange = {
	teamId: string;
	teamName: string;
	members: Array<{ email: string; roleDefId: string }>;
	added: string[];
	removed: string[];
	roleChanged?: Array<{ email: string; previousRoleDefId: string; roleDefId: string }>;
	/** email -> contributing group names, winner first. Absent on older payloads. */
	contributingGroupsByEmail?: Record<string, string[]>;
	/** @deprecated Pre-rename name. Read only as a fallback; see {@link contributingGroupsFor}. */
	sourceGroupsByEmail?: Record<string, string[]>;
};

export type SyncResultCompanyRoleChange = {
	added: Array<{ email: string; roleDefId: string }>;
	removed: string[];
	roleChanged: Array<{ email: string; previousRoleDefId: string; roleDefId: string }>;
	/** email -> contributing group names, winner first. Absent on payloads from an older deploy. */
	contributingGroupsByEmail?: Record<string, string[]>;
	/** @deprecated Pre-rename name. Read only as a fallback; see {@link contributingGroupsFor}. */
	sourceGroupsByEmail?: Record<string, string[]>;
};

export type SyncResultPayload = {
	dryRun: boolean;
	addedTotal?: number;
	removedTotal?: number;
	changesByTeam: SyncResultTeamChange[];
	changesByCompanyRole?: SyncResultCompanyRoleChange;
	warnings?: string[];
	errors?: string[];
};

export type DiffKind = 'added' | 'removed' | 'role' | 'issue';

export type DiffRow = {
	kind: DiffKind;
	/** Email for member changes, or the issue text when kind is 'issue'. */
	subject: string;
	group: string;
	team: string;
	teamId: string;
	/** Display form for the table: the destination role, or "previous -> next" for a role change. */
	role: string;
	/**
	 * The same information as `role`, kept apart so the export can give each one its own column.
	 * Collapsing them into one cell made a role change unsortable and unfilterable in a spreadsheet,
	 * which is most of what the export is for.
	 */
	previousRole: string;
	newRole: string;
	isCompanyScope: boolean;
};

export type RoleNameLookup = (roleDefId: string) => string;

const UNKNOWN_ROLE = 'name unknown';

/**
 * On-screen separator for a role change. Display only: the export reads previousRole and newRole,
 * so this string never reaches the CSV and can stay a real arrow.
 */
const ROLE_CHANGE_SEPARATOR = '→';

/**
 * The groups that put a member where they are, winner first, as one display string.
 *
 * Shared by team and company-role rows: a company role comes from a group just as a team assignment
 * does, and both payloads carry the field under the same name.
 *
 * `sourceGroupsByEmail` is the pre-rename name, still read so that a web deploy landing before the
 * functions deploy (or a dry run captured before either) keeps showing groups instead of silently
 * rendering every row as "-". Safe to drop once both sides have shipped.
 */
function contributingGroupsFor (
	change: { contributingGroupsByEmail?: Record<string, string[]>; sourceGroupsByEmail?: Record<string, string[]> },
	email: string,
): string {
	const byEmail = change.contributingGroupsByEmail ?? change.sourceGroupsByEmail;
	return (byEmail?.[email] ?? []).join('; ');
}

/**
 * The group an issue is about, for the reconcile's two `Group "<name>" ...` warnings (a skipped
 * group, and one whose assignmentType disagrees with its mapping).
 *
 * The reconcile embeds the name in the sentence instead of sending it as a field, and its redaction
 * step is documented to preserve exactly this shape while stripping the doc id that follows it. A
 * skipped group contributes no member rows, so without this its name reaches the admin only inside
 * the note: unfilterable on screen and absent from the export's source_group column.
 *
 * Returns '' for issues that name no group (a failed write, a company-role conflict), which leaves
 * those out of group-scoped views as before.
 */
function groupNameFromIssue (issue: string): string {
	return /Group "([^"]+)"/.exec(issue)?.[1] ?? '';
}

/**
 * @param teamRoleName resolves team role def ids; company-scope rows use companyRoleName instead,
 * because the two collections can hold different roles under the same id space.
 */
export function flattenSyncResult (
	payload: SyncResultPayload,
	teamRoleName: RoleNameLookup,
	companyRoleName: RoleNameLookup,
	companyScopeLabel = 'Company',
): DiffRow[] {
	const rows: DiffRow[] = [];

	(payload.changesByTeam || []).forEach((team: SyncResultTeamChange) => {
		const roleByEmail = new Map<string, string>((team.members || []).map(m => [m.email, m.roleDefId]));
		const teamName = team.teamName || team.teamId;

		(team.added || []).forEach((email: string) => {
			const newRole = teamRoleName(roleByEmail.get(email) || '');
			rows.push({
				kind: 'added',
				subject: email,
				group: contributingGroupsFor(team, email),
				team: teamName,
				teamId: team.teamId,
				role: newRole,
				previousRole: '',
				newRole,
				isCompanyScope: false,
			});
		});

		(team.roleChanged || []).forEach((change) => {
			const previousRole = teamRoleName(change.previousRoleDefId);
			const newRole = teamRoleName(change.roleDefId);
			rows.push({
				kind: 'role',
				subject: change.email,
				group: contributingGroupsFor(team, change.email),
				team: teamName,
				teamId: team.teamId,
				role: `${previousRole} ${ROLE_CHANGE_SEPARATOR} ${newRole}`,
				previousRole,
				newRole,
				isCompanyScope: false,
			});
		});

		(team.removed || []).forEach((email: string) => {
			// No group claims a removed member, so the group column is intentionally blank.
			rows.push({
				kind: 'removed',
				subject: email,
				group: '',
				team: teamName,
				teamId: team.teamId,
				role: '',
				previousRole: '',
				newRole: '',
				isCompanyScope: false,
			});
		});
	});

	const companyRole = payload.changesByCompanyRole;
	if (companyRole) {
		(companyRole.added || []).forEach((entry) => {
			const newRole = companyRoleName(entry.roleDefId);
			rows.push({
				kind: 'added',
				subject: entry.email,
				group: contributingGroupsFor(companyRole, entry.email),
				team: companyScopeLabel,
				teamId: '',
				role: newRole,
				previousRole: '',
				newRole,
				isCompanyScope: true,
			});
		});
		(companyRole.roleChanged || []).forEach((change) => {
			const previousRole = companyRoleName(change.previousRoleDefId);
			const newRole = companyRoleName(change.roleDefId);
			rows.push({
				kind: 'role',
				subject: change.email,
				group: contributingGroupsFor(companyRole, change.email),
				team: companyScopeLabel,
				teamId: '',
				role: `${previousRole} ${ROLE_CHANGE_SEPARATOR} ${newRole}`,
				previousRole,
				newRole,
				isCompanyScope: true,
			});
		});
		(companyRole.removed || []).forEach((email: string) => {
			rows.push({
				kind: 'removed',
				// Blank for the same reason as a removed team member: no group claims them any more,
				// which is exactly why the role is being taken away.
				group: '',
				subject: email,
				team: companyScopeLabel,
				teamId: '',
				role: '',
				previousRole: '',
				newRole: '',
				isCompanyScope: true,
			});
		});
	}

	getSyncIssues(payload).forEach((issue: string) => {
		rows.push({
			kind: 'issue',
			subject: issue,
			group: groupNameFromIssue(issue),
			team: '',
			teamId: '',
			role: '',
			previousRole: '',
			newRole: '',
			isCompanyScope: false,
		});
	});

	return rows;
}

/** Errors first: an error stopped something, a warning only skipped it. */
export function getSyncIssues (payload: SyncResultPayload): string[] {
	const errors = (payload.errors || []).map((e) => `Error: ${e}`);
	const warnings = (payload.warnings || []).map((w) => `Warning: ${w}`);
	return [...errors, ...warnings];
}

export type DiffCounts = { all: number; added: number; removed: number; role: number; issue: number };

export function countByKind (rows: DiffRow[]): DiffCounts {
	return rows.reduce((acc: DiffCounts, row: DiffRow) => {
		acc.all += 1;
		acc[row.kind] += 1;
		return acc;
	}, { all: 0, added: 0, removed: 0, role: 0, issue: 0 });
}

export type DiffFilters = { kind: DiffKind | 'all'; group: string; team: string; search: string };

export function filterDiffRows (rows: DiffRow[], filters: DiffFilters): DiffRow[] {
	const search = filters.search.trim().toLowerCase();
	return rows.filter((row: DiffRow) => {
		if (filters.kind !== 'all' && row.kind !== filters.kind) return false;
		// An issue that names a group carries it, so filtering to that group now surfaces the reason it
		// contributed nothing. The rest carry no group or team, and stay out of scoped views rather
		// than pretending they belong to whatever was selected.
		if (filters.group && row.group.split('; ').indexOf(filters.group) === -1) return false;
		if (filters.team && row.team !== filters.team) return false;
		if (search && row.subject.toLowerCase().indexOf(search) === -1) return false;
		return true;
	});
}

/** Distinct group names present in the diff, for the group filter. */
export function groupOptions (rows: DiffRow[]): string[] {
	const names = new Set<string>();
	rows.forEach((row: DiffRow) => {
		row.group.split('; ').forEach((name: string) => {
			if (name) names.add(name);
		});
	});
	return Array.from(names).sort((a, b) => a.localeCompare(b));
}

/** Distinct team (or company scope) names present in the diff, for the team filter. */
export function teamOptions (rows: DiffRow[]): string[] {
	const names = new Set<string>();
	rows.forEach((row: DiffRow) => {
		if (row.team) names.add(row.team);
	});
	return Array.from(names).sort((a, b) => a.localeCompare(b));
}

/**
 * Wraps a CSV field: doubles quotes and always quotes, so emails, names and notes stay in one cell.
 *
 * A leading =, +, - or @ is prefixed with a single quote first. Group and team names in this export
 * come from the customer's Identity Provider, and a spreadsheet treats a cell starting with any of
 * those as a formula to evaluate, so an unescaped name is a live payload in a file admins routinely
 * pass around. The prefix is the convention spreadsheets recognise as "this is text".
 */
function csvCell (value: string | number | null | undefined): string {
	const raw = String(value ?? '');
	const safe = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
	return `"${safe.replace(/"/g, '""')}"`;
}

const CSV_CHANGE_LABEL: Record<DiffKind, string> = {
	added: 'Added',
	removed: 'Removed',
	role: 'Role changed',
	issue: 'Skipped',
};

/**
 * One row per change. Exported from the same flattened list the dialog shows, so what an admin
 * reviews on screen and what they hand to someone else cannot drift apart.
 */
export function buildSyncResultCsv (rows: DiffRow[]): string {
	const header = ['scope', 'team', 'email', 'change', 'previous_role', 'new_role', 'source_group', 'notes'];
	const body = rows.map((row: DiffRow) => {
		if (row.kind === 'issue') {
			return ['Issue', '', '', CSV_CHANGE_LABEL.issue, '', '', row.group, row.subject];
		}
		return [
			row.isCompanyScope ? 'Company' : 'Team',
			row.isCompanyScope ? '' : row.team,
			row.subject,
			CSV_CHANGE_LABEL[row.kind],
			row.previousRole,
			row.newRole,
			row.group,
			'',
		];
	});
	return [header, ...body].map(row => row.map(csvCell).join(',')).join('\n');
}

export { ROLE_CHANGE_SEPARATOR, UNKNOWN_ROLE };
